(() => {
  if (window.YMM) return;

  const PREFIX = 'ymm';
  const UNIVERSAL = `${PREFIX}-universal`;
  const GARAGE_KEY = 'ymm:garage';
  const PENDING_KEY = 'ymm:pending';
  const DATA_TTL = 15 * 60 * 1000;
  const MAX_SAVED = 10;
  const inflight = new Map();

  const handleize = (text) =>
    String(text)
      .normalize('NFKD')
      .replace(/\p{M}/gu, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const tagFor = (year, make, model) => `${PREFIX}-${year}-${handleize(make)}-${handleize(model)}`;

  const store = {
    get(key, fallback, session) {
      try {
        const raw = (session ? sessionStorage : localStorage).getItem(key);
        return raw ? JSON.parse(raw) : fallback;
      } catch (e) {
        return fallback;
      }
    },
    set(key, value, session) {
      try {
        const s = session ? sessionStorage : localStorage;
        if (value === null) s.removeItem(key);
        else s.setItem(key, JSON.stringify(value));
      } catch (e) {}
    },
  };

  const garage = {
    read() {
      const g = store.get(GARAGE_KEY, null);
      return g && Array.isArray(g.vehicles) ? g : { current: null, vehicles: [] };
    },
    write(g) {
      store.set(GARAGE_KEY, g);
      document.dispatchEvent(new CustomEvent('ymm:change', { detail: g }));
    },
    current() {
      const g = this.read();
      return g.vehicles.find((v) => v.tag === g.current) || null;
    },
    select(vehicle) {
      const g = this.read();
      g.vehicles = [vehicle, ...g.vehicles.filter((v) => v.tag !== vehicle.tag)].slice(0, MAX_SAVED);
      g.current = vehicle.tag;
      this.write(g);
    },
    remove(tag) {
      const g = this.read();
      g.vehicles = g.vehicles.filter((v) => v.tag !== tag);
      if (g.current === tag) g.current = null;
      this.write(g);
    },
    clearCurrent() {
      const g = this.read();
      g.current = null;
      this.write(g);
    },
  };

  const vehicle = (year, make, model) => ({
    parts: [String(year), make, model],
    tag: tagFor(year, make, model),
  });
  const yearLabel = (value) => {
    const s = String(value);
    if (!s.startsWith('pre')) return s;
    const el = document.querySelector('[data-pre-label]');
    return (el ? el.dataset.preLabel : 'Pre __YEAR__').replace('__YEAR__', s.slice(3));
  };
  const isPre = (v) => Boolean(v) && String(v.parts[0]).startsWith('pre');
  const label = (v) => (v ? [yearLabel(v.parts[0]), v.parts[1], v.parts[2]].join(' ') : '');

  const produced = (m, year) => m.first <= year && year <= m.last && !m.skip.has(year);
  const producedIn = (data, m, value) =>
    String(value).startsWith('pre') ? m.first < data.groupBefore : produced(m, Number(value));

  const fetchJson = async (url) => {
    const r = await fetch(url, { credentials: 'same-origin' });
    if (!r.ok) throw new Error(`YMM data ${r.status}`);
    return JSON.parse(await r.text());
  };

  const normalise = (pages) => {
    const models = [];
    const seen = new Set();
    for (const page of pages) {
      for (const [make, model, first, last, skipped] of page.models || []) {
        if (!make || !model || !first) continue;
        const key = `${handleize(make)}-${handleize(model)}`;
        if (seen.has(key)) continue;
        seen.add(key);
        models.push({
          make: String(make).trim(),
          model: String(model).trim(),
          first: Number(first),
          last: Number(last) || Number(first),
          skip: new Set((Array.isArray(skipped) ? skipped : []).map(Number)),
        });
      }
    }
    const groupBefore = Number(pages[0] && pages[0].groupBefore) || 0;
    const years = [];
    if (models.length) {
      const newest = Math.max(...models.map((m) => m.last));
      const earliest = Math.max(Math.min(...models.map((m) => m.first)), groupBefore);
      for (let y = newest; y >= earliest; y--) {
        if (models.some((m) => produced(m, y))) years.push(String(y));
      }
      if (groupBefore && models.some((m) => m.first < groupBefore)) years.push(`pre${groupBefore}`);
    }
    return { models, years, groupBefore };
  };

  const loadData = (src) => {
    if (inflight.has(src)) return inflight.get(src);
    const cacheKey = `ymm:data:${src}`;
    const cached = store.get(cacheKey, null, true);
    if (cached && Date.now() - cached.t < DATA_TTL) {
      const p = Promise.resolve(normalise(cached.pages));
      inflight.set(src, p);
      return p;
    }
    const p = (async () => {
      const first = await fetchJson(src);
      const pages = [first];
      const sep = src.includes('?') ? '&' : '?';
      for (let n = 2; n <= (first.pages || 1); n++) pages.push(await fetchJson(`${src}${sep}page=${n}`));
      store.set(cacheKey, { t: Date.now(), pages }, true);
      return normalise(pages);
    })();
    inflight.set(src, p);
    p.catch(() => inflight.delete(src));
    return p;
  };

  const modelsFor = (data, value) => data.models.filter((m) => producedIn(data, m, value));

  const findVehicle = (data, tag) => {
    const m = /^ymm-(\d{4}|pre\d{4})-/.exec(tag);
    if (!m) return null;
    const year = m[1];
    const hit = modelsFor(data, year).find((x) => tagFor(year, x.make, x.model) === tag);
    return hit ? vehicle(year, hit.make, hit.model) : null;
  };

  // Bike results open sorted by Best selling (shoppers can still re-sort)
  const RESULTS_SORT = 'best-selling';

  const goTo = (baseUrl, tag) => {
    store.set(PENDING_KEY, tag, true);
    const url = new URL(baseUrl, location.origin);
    url.pathname = `${url.pathname.replace(/\/+$/, '')}/${tag}`;
    url.search = `?sort_by=${RESULTS_SORT}`;
    location.assign(url.toString());
  };

  const sortText = (a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

  class YmmSelector extends HTMLElement {
    async connectedCallback() {
      this.selects = ['year', 'make', 'model'].map((k) => this.querySelector(`[data-level="${k}"]`));
      this.submit = this.querySelector('[type="submit"]');
      this.reset = this.querySelector('[type="reset"]');
      this.garageList = this.querySelector('[data-garage]');
      this.status = this.querySelector('[data-status]');
      const form = this.querySelector('form');
      form.addEventListener('submit', (e) => this.onSubmit(e));
      form.addEventListener('reset', (e) => this.onReset(e));
      this.selects.forEach((s, i) => s.addEventListener('change', () => this.onChange(i)));
      this.onGarageChange = () => {
        this.renderGarage();
        this.syncToCurrent();
      };
      document.addEventListener('ymm:change', this.onGarageChange);
      this.renderGarage();
      try {
        this.data = await loadData(this.dataset.src);
      } catch (e) {
        this.status.textContent = this.dataset.errorText;
        return;
      }
      this.fill(0);
      const current = garage.current();
      if (current) this.prefill(current.parts);
    }

    disconnectedCallback() {
      document.removeEventListener('ymm:change', this.onGarageChange);
    }

    options(level) {
      const [year, make] = this.selects.map((s) => s.value);
      if (level === 0) return this.data.years;
      const models = modelsFor(this.data, year);
      if (level === 1) return [...new Set(models.map((m) => m.make))].sort(sortText);
      return models.filter((m) => m.make === make).map((m) => m.model).sort(sortText);
    }

    fill(level) {
      this.selects.forEach((select, i) => {
        if (i < level) return;
        const values = i === level ? this.options(i) : [];
        select.replaceChildren(new Option(select.dataset.placeholder, ''));
        values.forEach((v) => select.add(new Option(i === 0 ? yearLabel(v) : v, v)));
        select.disabled = values.length === 0;
      });
      this.updateButtons();
    }

    // Find parts turns "ready" (theme orange) only when all three fields are set;
    // Reset is available as soon as anything is selected.
    updateButtons() {
      const complete = this.selects.every((s) => s.value);
      this.submit.disabled = !complete;
      this.submit.classList.toggle('is-ready', complete);
      if (this.reset) this.reset.disabled = !this.selects.some((s) => s.value);
    }

    onChange(level) {
      if (level < 2) {
        this.fill(level + 1);
        if (this.selects[level].value) this.selects[level + 1].focus();
      }
      this.updateButtons();
    }

    // Clears all three dropdowns. Saved bikes stay in the garage, but none is
    // marked as current, so the fields don't refill on the next page load.
    onReset(event) {
      event.preventDefault();
      if (!this.data) return;
      this.status.textContent = '';
      this.fill(0);
      if (garage.current()) garage.clearCurrent();
      this.selects[0].focus();
    }

    syncToCurrent() {
      const current = garage.current();
      if (!this.data || !current) return;
      if (this.selects.every((s, i) => s.value === current.parts[i])) return;
      this.fill(0);
      this.prefill(current.parts);
    }

    prefill(parts) {
      parts.forEach((p, i) => {
        const select = this.selects[i];
        if (![...select.options].some((o) => o.value === p)) return;
        select.value = p;
        if (i < 2) this.fill(i + 1);
      });
      this.updateButtons();
    }

    onSubmit(event) {
      event.preventDefault();
      const [year, make, model] = this.selects.map((s) => s.value);
      if (!year || !make || !model) return;
      this.choose(vehicle(year, make, model));
    }

    choose(v) {
      garage.select(v);
      if (this.dataset.stay === 'true') {
        this.status.textContent = this.dataset.savedText;
        const details = this.closest('details');
        if (details) details.open = false;
        return;
      }
      goTo(this.dataset.baseUrl, v.tag);
    }

    renderGarage() {
      if (!this.garageList) return;
      const g = garage.read();
      this.garageList.closest('[data-garage-wrap]').hidden = g.vehicles.length === 0;
      this.garageList.replaceChildren(
        ...g.vehicles.map((v) => {
          const li = document.createElement('li');
          li.className = 'ymm-garage__item';
          if (v.tag === g.current) li.classList.add('is-current');
          const pick = document.createElement('button');
          pick.type = 'button';
          pick.className = 'ymm-garage__pick';
          pick.textContent = label(v);
          if (v.tag === g.current) pick.setAttribute('aria-current', 'true');
          pick.addEventListener('click', () => {
            if (this.data) this.prefill(v.parts);
            this.choose(v);
          });
          const del = document.createElement('button');
          del.type = 'button';
          del.className = 'ymm-garage__remove';
          del.setAttribute('aria-label', `${this.dataset.removeText} ${label(v)}`);
          del.textContent = '×';
          del.addEventListener('click', () => garage.remove(v.tag));
          li.append(pick, del);
          return li;
        })
      );
    }
  }

  class YmmFitment extends HTMLElement {
    connectedCallback() {
      const tags = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
      this.tags = new Set(tags.filter((t) => t.startsWith(`${PREFIX}-`)));
      this.onGarageChange = () => this.render();
      document.addEventListener('ymm:change', this.onGarageChange);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('ymm:change', this.onGarageChange);
    }

    show(state, name) {
      const note = this.querySelector('[data-pre-note]');
      if (note) note.hidden = true;
      this.querySelectorAll('[data-state]').forEach((el) => {
        el.hidden = el.dataset.state !== state;
      });
      this.querySelectorAll(`[data-state="${state}"] [data-vehicle]`).forEach((el) => {
        el.textContent = name || '';
      });
      this.dataset.result = state;
    }

    render() {
      if (this.tags.has(UNIVERSAL)) return this.show('universal');
      const current = garage.current();
      if (!current) return this.show('none');
      const fits = this.tags.has(current.tag);
      this.show(fits ? 'fits' : 'nofit', label(current));
      const note = this.querySelector('[data-pre-note]');
      if (note) note.hidden = !(fits && isPre(current));
    }
  }

  class YmmResults extends HTMLElement {
    async connectedCallback() {
      const active = this.dataset.activeTag;
      const pending = store.get(PENDING_KEY, null, true);
      store.set(PENDING_KEY, null, true);
      const current = garage.current();
      this.querySelectorAll('[data-clear]').forEach((a) => a.addEventListener('click', () => garage.clearCurrent()));
      this.querySelectorAll('[data-picker-toggle]').forEach((b) => b.addEventListener('click', () => this.togglePicker()));
      if (active) {
        let v = current && current.tag === active ? current : null;
        if (!v) {
          try {
            v = findVehicle(await loadData(this.dataset.src), active);
            if (v) garage.select(v);
          } catch (e) {}
        }
        if (v) this.relabel(active, label(v));
        return this.show('active', v ? label(v) : active);
      }
      if (current && pending === current.tag) return this.show('empty', label(current), current.tag);
      this.show('idle');
    }

    // Phones only: the selector below the bar is folded away until "Change bike" is tapped.
    togglePicker(open) {
      const wrap = this.closest('[data-ymm-wrap]') || this.parentElement;
      const picker = wrap && wrap.querySelector('[data-picker]');
      if (!picker) return;
      const isOpen = typeof open === 'boolean' ? open : !picker.classList.contains('is-open');
      picker.classList.toggle('is-open', isOpen);
      this.querySelectorAll('[data-picker-toggle]').forEach((b) => b.setAttribute('aria-expanded', String(isOpen)));
      if (isOpen && open === undefined) {
        const first = picker.querySelector('select:not([disabled])');
        if (first) first.focus();
      }
    }

    relabel(tag, name) {
      if (document.title.includes(tag)) document.title = document.title.replace(tag, name);
      document.querySelectorAll('[class*="breadcrumb"]').forEach((el) => {
        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
        while (walker.nextNode()) {
          const node = walker.currentNode;
          if (node.nodeValue.includes(tag)) node.nodeValue = node.nodeValue.replace(tag, name);
        }
      });
    }

    show(state, name, tag) {
      this.querySelectorAll('[data-state]').forEach((el) => {
        el.hidden = el.dataset.state !== state;
      });
      this.querySelectorAll(`[data-state="${state}"] [data-vehicle]`).forEach((el) => {
        el.textContent = name || '';
      });
      const link = this.querySelector(`[data-state="${state}"] [data-vehicle-link]`);
      if (link && tag) link.href = `${this.dataset.collectionUrl.replace(/\/+$/, '')}/${tag}?sort_by=${RESULTS_SORT}`;
      if (state === 'empty') this.togglePicker(true);
      this.hidden = state === 'idle';
      const wrap = this.closest('[data-ymm-wrap]');
      if (wrap) wrap.hidden = state === 'idle';
      this.dataset.result = state;
    }
  }

  window.YMM = { handleize, tagFor, garage, loadData, findVehicle };
  customElements.define('ymm-selector', YmmSelector);
  customElements.define('ymm-fitment', YmmFitment);
  customElements.define('ymm-results', YmmResults);
})();
